import { Box, Flex, HStack, Image, Portal, Skeleton, Text, useBreakpointValue, VStack } from '@chakra-ui/react'
import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useLocation } from 'react-router-dom'
import { X, Search, Users, ShieldAlert, Package, Link2, BarChart3, Globe, Store } from 'lucide-react'
import krakenIcon from '../../../../assets/kraken-icon.png'
import { useKrakenContextOptional } from './KrakenContext'
import KrakenChatBar from './KrakenChatBar'
import KrakenChatMessage from './KrakenChatMessage'
import type { Message } from './KrakenChatMessage'
import KrakenNetworkTree from './KrakenNetworkTree'
import type { KrakenGraphResponse, KrakenStreamEvent } from '../../../../types/kraken.types'
import { useKrakenNetworkFetch, useKrakenChatStream } from '../../../../hooks/useKraken'
import { useAuth } from '../../../../hooks/useAuth'

const MotionBox = motion(Box)

type EntityContext = {
  type: 'item' | 'seller' | 'saw'
  id: string
  name?: string
} | null

const getSuggestions = (type: 'item' | 'seller' | 'saw') => {
  const suggestions = {
    item: [
      { label: 'Similar listings', query: 'Find similar items in the network', icon: Search },
      { label: 'Seller connections', query: 'What sellers are connected to this item?', icon: Users },
      { label: 'Risk assessment', query: 'Assess the risk level of this item', icon: ShieldAlert },
    ],
    seller: [
      { label: 'All items', query: 'Show all items from this seller', icon: Package },
      { label: 'Related sellers', query: 'Find sellers connected to this one', icon: Link2 },
      { label: 'Patterns', query: "Analyze this seller's network patterns", icon: BarChart3 },
    ],
    saw: [
      { label: 'Hosted items', query: 'What items are hosted on this site?', icon: Store },
      { label: 'Connected sellers', query: 'Which sellers use this website?', icon: Users },
      { label: 'Site risk', query: 'Analyze the risk of this website', icon: Globe },
    ],
  }
  return suggestions[type] || []
}

const KrakenGlobalFab = () => {
  const { permissions } = useAuth()
  const { fetchNetwork } = useKrakenNetworkFetch()
  const { streamChat } = useKrakenChatStream()
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [isStreaming, setIsStreaming] = useState(false)
  const [isIndexed, setIsIndexed] = useState(false)
  const [networkData, setNetworkData] = useState<KrakenGraphResponse | null>(null)
  const networkContextRef = useRef<KrakenGraphResponse | null>(null)
  const hasStartedInvestigation = useRef(false)
  const lastContextRef = useRef<string | null>(null)
  const [hideFab, setHideFab] = useState(false)
  const isMobile = useBreakpointValue({ base: true, md: false }, { ssr: false }) ?? false
  const [mobileView, setMobileView] = useState<'chat' | 'graph'>('chat')

  // Hide fab when a fullscreen/overlay modal requests it (e.g. notice send modals)
  useEffect(() => {
    const check = () => setHideFab(document.body.hasAttribute('data-hide-kraken-fab'))
    const observer = new MutationObserver(check)
    observer.observe(document.body, { attributes: true, attributeFilter: ['data-hide-kraken-fab'] })
    return () => observer.disconnect()
  }, [])

  const krakenContext = useKrakenContextOptional()
  const currentEntity = krakenContext?.currentEntity
  const location = useLocation()

  const urlContext = useMemo((): EntityContext => {
    const path = location.pathname
    const itemMatch = path.match(/\/react\/items\/([^/]+)/)
    if (itemMatch) {
      const id = itemMatch[1]
      if (id && !['overview', 'list', 'imports', 'exports'].some((x) => id.startsWith(x))) {
        return { type: 'item', id }
      }
    }
    const sellerMatch = path.match(/\/react\/sellers\/([^/]+)/)
    if (sellerMatch) {
      const id = sellerMatch[1]
      if (id && !['list', 'platform', 'standalone'].includes(id)) {
        return { type: 'seller', id }
      }
    }
    const sawMatch = path.match(/\/react\/saw\/sites\/([^/]+)/)
    if (sawMatch) {
      const id = sawMatch[1]
      if (id) return { type: 'saw', id }
    }
    return null
  }, [location.pathname])

  const context: EntityContext = currentEntity
    ? { type: currentEntity.type, id: currentEntity.id, name: currentEntity.name }
    : urlContext

  const fullId = currentEntity?.fullId || (context ? `${context.type}_${context.id}` : undefined)
  const contextKey = context ? `${context.type}-${context.id}` : null
  const hasStartedChat = messages.length > 0

  useEffect(() => {
    if (contextKey !== lastContextRef.current) {
      lastContextRef.current = contextKey
      hasStartedInvestigation.current = false
      setMessages([])
      setIsIndexed(false)
      setNetworkData(null)
      networkContextRef.current = null
    }
  }, [contextKey])

  useEffect(() => {
    if (isOpen && context && !hasStartedInvestigation.current) {
      hasStartedInvestigation.current = true
      indexEntity(context)
    }
  }, [isOpen, context])

  const indexEntity = async (ctx: NonNullable<EntityContext>) => {
    setIsStreaming(true)
    try {
      const data = await fetchNetwork(`${ctx.type}_${ctx.id}`)
      networkContextRef.current = data
      setNetworkData(data)
    } catch (error) {
      // silently handle indexing failure
    } finally {
      setIsIndexed(true)
      setIsStreaming(false)
    }
  }

  const handleSendMessage = useCallback(
    async (content: string) => {
      if (!content.trim()) return
      const userMsg: Message = { id: `user-${Date.now()}`, content, role: 'user', timestamp: new Date() }
      setMessages((prev) => [...prev, userMsg])
      setIsStreaming(true)
      const assistantId = `assistant-${Date.now()}`
      setMessages((prev) => [...prev, { id: assistantId, content: '', role: 'assistant', timestamp: new Date() }])
      try {
        const chatMessages = [...messages, userMsg]
          .filter((msg) => msg.content.trim())
          .map((msg) => ({ role: msg.role as 'user' | 'assistant', content: msg.content }))
        const networkContext = networkContextRef.current
          ? {
              nodes: networkContextRef.current.nodes,
              edges: networkContextRef.current.edges,
              root: networkContextRef.current.root,
            }
          : null
        let streamedContent = ''
        await streamChat(
          { messages: chatMessages, network_context: networkContext },
          (event: KrakenStreamEvent) => {
            if (event.type === 'status') {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantId ? { ...msg, content: streamedContent || event.content } : msg,
                ),
              )
            } else if (event.type === 'chunk') {
              streamedContent += event.content
              setMessages((prev) =>
                prev.map((msg) => (msg.id === assistantId ? { ...msg, content: streamedContent } : msg)),
              )
            } else if (event.type === 'error') {
              setMessages((prev) =>
                prev.map((msg) => (msg.id === assistantId ? { ...msg, content: `Error: ${event.content}` } : msg)),
              )
            }
          },
        )
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred'
        setMessages((prev) =>
          prev.map((msg) => (msg.id === assistantId ? { ...msg, content: `Error: ${errorMessage}` } : msg)),
        )
      } finally {
        setIsStreaming(false)
      }
    },
    [messages],
  )

  const handleSuggestionClick = (query: string) => {
    handleSendMessage(query)
  }

  // Disable Chakra overlay pointer-events so they don't intercept our panels
  useEffect(() => {
    if (!isOpen) return
    const style = document.createElement('style')
    style.id = 'ocean-ai-fab-layer-fix'
    style.textContent = `
      .chakra-modal__overlay { pointer-events: none !important; }
      .chakra-modal__content-container { pointer-events: none !important; }
      .chakra-modal__content { pointer-events: auto !important; }
      [data-scope="dialog"][data-part="backdrop"] { pointer-events: none !important; }
    `
    document.head.appendChild(style)
    return () => {
      style.remove()
    }
  }, [isOpen])

  const showGraph = isIndexed && context

  // --- RENDER ---

  const fabButton =
    !isOpen && !hideFab && context ? (
      <AnimatePresence>
        <MotionBox
          position="fixed"
          bottom="24px"
          right="24px"
          zIndex={2147483647}
          cursor="pointer"
          onClick={() => setIsOpen(true)}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ type: 'spring', damping: 20, stiffness: 300 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <HStack
            h="40px"
            px={4}
            spacing={2}
            borderRadius="full"
            bg="white"
            boxShadow="0 4px 24px -4px rgba(0, 0, 0, 0.15), 0 2px 12px -2px rgba(0, 0, 0, 0.08)"
            border="1.5px solid"
            borderColor="rgba(0, 0, 0, 0.08)"
            _hover={{ boxShadow: '0 8px 32px -4px rgba(0, 0, 0, 0.18), 0 4px 16px -2px rgba(0, 0, 0, 0.1)' }}
          >
            <Image src={krakenIcon} alt="Ocean AI" w="24px" h="24px" objectFit="contain" />
            <Text fontSize="13px" fontWeight="600" color="gray.700" whiteSpace="nowrap">
              {context.type === 'item'
                ? 'Analyze this item'
                : context.type === 'seller'
                  ? 'Analyze this seller'
                  : 'Analyze this site'}
            </Text>
          </HStack>
        </MotionBox>
      </AnimatePresence>
    ) : null

  const panels = isOpen ? (
    <>
      {/* Single backdrop — visual + click-to-close, but allows scroll-through */}
      <Box
        position="fixed"
        inset={0}
        zIndex={2147483645}
        bg={isMobile ? 'rgba(10, 10, 20, 0.55)' : 'blackAlpha.200'}
        sx={isMobile ? { backdropFilter: 'blur(6px)' } : undefined}
        onClick={() => setIsOpen(false)}
        css={{
          animation: 'fabFadeIn 0.15s ease-out',
          '@keyframes fabFadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
        }}
      />

      {/* Panel container — NO transform, NO framer-motion */}
      <Box
        position="fixed"
        top={isMobile ? '52px' : '20px'}
        right={isMobile ? '0' : '20px'}
        bottom={isMobile ? '0' : '20px'}
        left={isMobile ? '0' : undefined}
        h={isMobile ? 'auto' : 'calc(100vh - 40px)'}
        zIndex={2147483647}
        display="flex"
        flexDirection={isMobile ? 'column' : 'row'}
        gap={isMobile ? '0' : '12px'}
        pointerEvents="none"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        bg={isMobile ? 'white' : 'transparent'}
        borderTopRadius={isMobile ? '24px' : '0'}
        overflow={isMobile ? 'hidden' : 'visible'}
        boxShadow={isMobile ? '0 -8px 32px -8px rgba(15, 23, 42, 0.18)' : 'none'}
        css={{
          animation: isMobile ? 'fabSheetUp 0.3s cubic-bezier(0.32, 0.72, 0, 1)' : 'fabPanelsIn 0.25s ease-out',
          '@keyframes fabPanelsIn': { from: { opacity: 0 }, to: { opacity: 1 } },
          '@keyframes fabSheetUp': {
            from: { opacity: 0, transform: 'translateY(24px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
          },
        }}
      >
        {/* Mobile drag handle + tab switcher */}
        {isMobile && (
          <Box pointerEvents="auto" flexShrink={0} bg="white">
            {/* Drag handle */}
            <Flex justify="center" pt="10px" pb="4px">
              <Box w="40px" h="5px" borderRadius="full" bg="neutral.200" />
            </Flex>

            {/* Tab switcher row */}
            <Flex
              px="8px"
              pt="4px"
              pb="8px"
              gap="6px"
              align="center"
              borderBottomWidth="1px"
              borderColor="neutral.100"
            >
              <Flex
                flex="1"
                bg="neutral.50"
                borderRadius="10px"
                p="3px"
                gap="2px"
              >
                {([
                  { key: 'chat' as const, label: 'Ocean AI' },
                  { key: 'graph' as const, label: 'O2O Graph' },
                ]).map((tab) => {
                  const isActiveTab = mobileView === tab.key
                  return (
                    <Box
                      key={tab.key}
                      as="button"
                      flex="1"
                      py="9px"
                      borderRadius="8px"
                      fontSize="13px"
                      fontWeight={isActiveTab ? '600' : '500'}
                      color={isActiveTab ? 'neutral.900' : 'neutral.500'}
                      bg={isActiveTab ? 'white' : 'transparent'}
                      boxShadow={isActiveTab ? '0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04)' : 'none'}
                      transition="all 0.18s ease-out"
                      sx={{ WebkitTapHighlightColor: 'transparent' }}
                      _active={{ transform: 'scale(0.98)' }}
                      onClick={() => setMobileView(tab.key)}
                    >
                      {tab.label}
                    </Box>
                  )
                })}
              </Flex>
              <Box
                as="button"
                w="40px"
                h="40px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                color="neutral.500"
                borderRadius="full"
                bg="neutral.50"
                sx={{ WebkitTapHighlightColor: 'transparent' }}
                _active={{ transform: 'scale(0.94)', bg: 'neutral.100' }}
                transition="all 0.15s ease-out"
                onClick={() => setIsOpen(false)}
                aria-label="Close"
                flexShrink={0}
              >
                <X size={18} />
              </Box>
            </Flex>
          </Box>
        )}

        {/* LEFT — O2O Graph Panel */}
        {showGraph && (!isMobile || mobileView === 'graph') && (
          <Box
            width={isMobile ? '100%' : '55vw'}
            maxWidth={isMobile ? 'none' : '960px'}
            minWidth={isMobile ? 'auto' : '480px'}
            h={isMobile ? '100%' : '100%'}
            flex={isMobile ? '1' : 'none'}
            bg="white"
            boxShadow={isMobile ? 'none' : '0 8px 40px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.05)'}
            borderRadius={isMobile ? '0' : '16px'}
            overflow="hidden"
            position="relative"
            display="flex"
            pointerEvents="auto"
            onWheel={(e: React.WheelEvent) => e.stopPropagation()}
            flexDirection="column"
          >
            <HStack px={4} pt={3} pb={2} spacing={2}>
              <Text fontSize="11px" fontWeight="700" color="gray.500" letterSpacing="0.05em">
                O2O
              </Text>
              {networkData && networkData.nodes.length > 0 ? (
                <HStack spacing={1}>
                  <Box w="6px" h="6px" borderRadius="full" bg="green.400" />
                  <Text fontSize="10px" color="neutral.400" fontWeight="500">
                    {networkData.nodes?.length ?? 0} nodes &middot; {networkData.edges?.length ?? 0} edges
                  </Text>
                </HStack>
              ) : (
                <HStack spacing={1}>
                  <Box w="6px" h="6px" borderRadius="full" bg="gray.300" />
                  <Text fontSize="10px" color="neutral.400" fontWeight="500">
                    No network data
                  </Text>
                </HStack>
              )}
            </HStack>
            <Box flex={1} minH={0} display="flex" flexDirection="column">
              {networkData && networkData.nodes.length > 0 ? (
                <KrakenNetworkTree
                  networkData={networkData}
                  isLoading={false}
                  rootId={fullId}
                  rootType={context?.type}
                />
              ) : (
                <VStack flex={1} justify="center" align="center" spacing={2} color="gray.400">
                  <Globe size={32} strokeWidth={1.2} />
                  <Text fontSize="13px" fontWeight="500">
                    Not yet indexed in the network graph
                  </Text>
                  <Text fontSize="11px" color="gray.300">
                    Chat with Ocean AI using the panel on the right
                  </Text>
                </VStack>
              )}
            </Box>
          </Box>
        )}

        {/* RIGHT — Chat Panel — FLAT structure: panel > scrollable | footer */}
        <Box
          w={isMobile ? '100%' : '25vw'}
          minW={isMobile ? 'auto' : '380px'}
          maxW={isMobile ? 'none' : '480px'}
          h={isMobile ? '100%' : '100%'}
          flex={isMobile ? '1' : 'none'}
          display={!isMobile || mobileView === 'chat' ? 'flex' : 'none'}
          bg="white"
          boxShadow={isMobile ? 'none' : '0 8px 40px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.05)'}
          borderRadius={isMobile ? '0' : '16px'}
          position="relative"
          pointerEvents="auto"
          onWheel={(e: React.WheelEvent) => e.stopPropagation()}
          sx={{
            flexDirection: 'column',
            overflow: 'hidden',
            paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : undefined,
          }}
        >
          {/* Close button (absolute — desktop only; mobile uses tab-bar close) */}
          {!isMobile && (
            <Box
              position="absolute"
              top={3}
              right={3}
              zIndex={20}
              cursor="pointer"
              color="gray.400"
              _hover={{ color: 'gray.600' }}
              p={1}
              borderRadius="full"
              onClick={() => setIsOpen(false)}
            >
              <X size={16} />
            </Box>
          )}
          {/* Accent line (absolute — does not affect flex) */}
          <Box
            position="absolute"
            top={0}
            left={0}
            bottom={0}
            w="3px"
            bg="gray.300"
            borderTopLeftRadius="16px"
            borderBottomLeftRadius="16px"
          />
          {/* Dotted background (absolute — does not affect flex) */}
          <Box
            position="absolute"
            inset={0}
            zIndex={0}
            pointerEvents="none"
            bgImage="radial-gradient(rgba(0, 0, 0, 0.05) 1.5px, transparent 1.5px)"
            bgSize="12px 12px"
          />
          <Box
            position="absolute"
            inset={0}
            zIndex={1}
            pointerEvents="none"
            bgImage="linear-gradient(to top, transparent 0%, rgba(255,255,255,0.6) 100%)"
          />

          {/* === CONTENT AREA — position:relative so the absolute child fills it === */}
          <Box
            sx={{
              flex: '1 1 0%',
              minHeight: 0,
              position: 'relative',
              zIndex: 2,
            }}
          >
            {/* Absolute-positioned scroll container — bypasses flex min-height issues */}
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              overflowY="auto"
              overflowX="hidden"
              css={{
                WebkitOverflowScrolling: 'touch',
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': { background: 'rgba(124, 58, 237, 0.15)', borderRadius: '3px' },
                '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(124, 58, 237, 0.25)' },
              }}
            >
              {/* Indexing skeleton */}
              {context && isStreaming && !isIndexed && (
                <Box mx={3} mt={3} mb={2}>
                  <Box
                    bg="white"
                    borderRadius="12px"
                    boxShadow="0 2px 12px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)"
                    p={4}
                  >
                    <HStack spacing={2} mb={3}>
                      <Box
                        w="8px"
                        h="8px"
                        borderRadius="full"
                        bg="gray.400"
                        css={{
                          animation: 'pulse 1.5s ease-in-out infinite',
                          '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.4 } },
                        }}
                      />
                      <Text fontSize="12px" color="gray.500" fontWeight="500">
                        Indexing {context.type}...
                      </Text>
                    </HStack>
                    <VStack spacing={2} align="stretch">
                      <Skeleton height="14px" borderRadius="6px" />
                      <Skeleton height="14px" borderRadius="6px" w="75%" />
                      <Skeleton height="14px" borderRadius="6px" w="55%" />
                    </VStack>
                  </Box>
                </Box>
              )}

              {hasStartedChat ? (
                <Box px={4} py={6}>
                  {messages.map((message, index) => (
                    <Box key={message.id} mb={4}>
                      <KrakenChatMessage
                        message={message.content}
                        isUser={message.role === 'user'}
                        isStreaming={isStreaming && index === messages.length - 1 && message.role === 'assistant'}
                      />
                    </Box>
                  ))}
                  <Box h="120px" />
                </Box>
              ) : (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  h="100%"
                  px={6}
                  py={8}
                >
                  <HStack spacing={2} mb={2}>
                    <Image src={krakenIcon} alt="Ocean AI" w="44px" h="44px" objectFit="contain" />
                    <Text fontSize="20px" fontWeight="700" color="gray.700" letterSpacing="-0.02em">
                      OCEAN AI
                    </Text>
                  </HStack>
                  {context && isIndexed && (
                    <HStack spacing={2} flexWrap="wrap" justify="center" mb={2} maxW="320px">
                      {getSuggestions(context.type).map((suggestion, i) => {
                        const Icon = suggestion.icon
                        return (
                          <HStack
                            key={i}
                            spacing={1.5}
                            px={3}
                            py={1.5}
                            bg="white"
                            borderRadius="full"
                            border="1px solid"
                            borderColor="gray.200"
                            cursor="pointer"
                            _hover={{ borderColor: 'gray.300', bg: 'gray.50' }}
                            transition="all 0.15s"
                            onClick={() => handleSuggestionClick(suggestion.query)}
                          >
                            <Box color="gray.400" flexShrink={0}>
                              <Icon size={13} />
                            </Box>
                            <Text fontSize="12px" color="neutral.600" fontWeight="500" whiteSpace="nowrap">
                              {suggestion.label}
                            </Text>
                          </HStack>
                        )
                      })}
                    </HStack>
                  )}
                </Box>
              )}
            </Box>
          </Box>

          {/* === FIXED FOOTER — chat input bar === */}
          <Box
            flexShrink={0}
            position="relative"
            zIndex={3}
            _before={{
              content: '""',
              position: 'absolute',
              top: '-60px',
              left: 0,
              right: 0,
              h: '60px',
              pointerEvents: 'none',
              bgImage: 'linear-gradient(to bottom, transparent 0%, rgba(255,255,255,0.95) 70%, white 100%)',
            }}
          >
            <KrakenChatBar
              onSendMessage={handleSendMessage}
              disabled={hasStartedChat ? isStreaming : isStreaming && !isIndexed}
            />
          </Box>
        </Box>
      </Box>
    </>
  ) : null

  if (!permissions?.isSuperAdmin) return null

  return (
    <Portal appendToParentPortal={false}>
      {fabButton}
      {panels}
    </Portal>
  )
}

export default KrakenGlobalFab
